import React, { useState, useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import ModalWrapper from '../ModalWrapper'
import Button from '../../uiKit/Button'
import Icon from '../../misc/Icons'
import { verifyEmailOtp, resendEmailOtp } from '../../../actions/session'
import getErrorMessageForRequest from '../../../utils/getErrorMessageForRequest'

const OTP_LENGTH = 6

const EmailOtpModal = (props) => {
  const {
    hideModal, authFlow, email, verifyEmailOtp, resendEmailOtp,
  } = props
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''))
  const [submitLoading, setSubmitLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [emailOtpEventId, setEmailOtpEventId] = useState(authFlow.emailOtpEventId)
  const inputRefs = useRef([])

  useEffect(() => {
    if (resendCooldown <= 0) return undefined
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  function handleChange(index, value) {
    const char = value.replace(/\D/g, '').slice(-1)
    const newDigits = [...digits]
    newDigits[index] = char
    setDigits(newDigits)
    if (char && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1].focus()
    } else if (char && index === OTP_LENGTH - 1 && newDigits.every((d) => d)) {
      handleSubmitDigits(newDigits)
    }
  }

  function handleKeyDown(index, e) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1].focus()
    }
  }

  function handlePaste(e) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    const newDigits = [...digits]
    pasted.split('').forEach((char, i) => { newDigits[i] = char })
    setDigits(newDigits)
    const nextEmpty = newDigits.findIndex((d) => !d)
    const focusIndex = nextEmpty === -1 ? OTP_LENGTH - 1 : nextEmpty
    inputRefs.current[focusIndex].focus()
    if (nextEmpty === -1) {
      handleSubmitDigits(newDigits)
    }
  }

  async function handleSubmitDigits(currentDigits) {
    const otpCode = currentDigits.join('')
    if (otpCode.length < OTP_LENGTH) return
    try {
      setSubmitLoading(true)
      setErrorMessage('')
      await verifyEmailOtp(emailOtpEventId, otpCode)
      hideModal()
    } catch (err) {
      setSubmitLoading(false)
      const message = getErrorMessageForRequest({
        requestName: 'verifyEmailOtp',
        response: err.response,
      })
      setErrorMessage(message)
    }
  }

  function handleSubmit(e) {
    e.preventDefault()
    handleSubmitDigits(digits)
  }

  async function handleResend() {
    try {
      setResendLoading(true)
      setResendCooldown(0)
      setErrorMessage('')
      const response = await resendEmailOtp(
        authFlow.id,
        { token: authFlow.token },
      )
      const emailOtpStep = response.nextSteps?.find((it) => it.eventName === 'email-otp')
      setEmailOtpEventId(emailOtpStep.eventId)
      setResendCooldown(30)
    } catch (err) {
      const message = getErrorMessageForRequest({
        requestName: 'verifyEmailOtp',
        response: err.response,
      })
      setErrorMessage(message)
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className='emailOtp-modalContainer marketing'>
      <ModalWrapper
        hideModal={hideModal}
        closeBtn={false}
        cancelBtn={false}
        showModalHeader={false}
        modalStyle='dark'
      >
        <div className='emailOtp-modalContent'>
          <button
            type='button'
            className='emailOtp-modalContent-backBtn'
            onClick={hideModal}
          >
            ← Back
          </button>
          <div className='emailOtp-modalContent-iconBadge'>
            <Icon name='emailOtp' />
          </div>
          <div className='emailOtp-modalContent-title'>
            Verify your email
          </div>
          <div className='emailOtp-modalContent-description'>
            {'The verification code has been sent to your email '}
            <span className='emailOtp-modalContent-description-email'>{email}</span>
            <div className='emailOtp-modalContent-description-expiry'>
              This code expires in 10 minutes.
            </div>
          </div>
          <form className='emailOtp-modalContent-main' onSubmit={handleSubmit}>
            <div className='emailOtp-modalContent-digitRow'>
              {digits.map((digit, index) => (
                <input
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el }}
                  className='emailOtp-modalContent-digitInput'
                  type='text'
                  inputMode='numeric'
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  maxLength={1}
                  value={digit}
                  data-filled={digit ? 'true' : 'false'}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onFocus={(e) => e.target.select()}
                  onPaste={handlePaste}
                  autoFocus={index === 0}
                />
              ))}
            </div>
            {errorMessage && (
              <div className='emailOtp-modalContent-error'>
                {errorMessage}
              </div>
            )}
            <div className='emailOtp-modalContent-resend'>
              {resendCooldown > 0 ? (
                <span className='emailOtp-modalContent-resend-success'>
                  {`Sent! You can resend in ${resendCooldown}s`}
                </span>
              ) : (
                <button
                  type='button'
                  className='emailOtp-modalContent-resend-btn'
                  onClick={handleResend}
                  disabled={resendLoading}
                >
                  {resendLoading ? 'Sending...' : 'Resend code'}
                </button>
              )}
            </div>
            <Button
              size='large'
              color='blue'
              label='Continue'
              type='submit'
              submitLoading={submitLoading}
              onClickFunction={() => handleSubmit}
            />
          </form>
        </div>
      </ModalWrapper>
    </div>
  )
}

export default connect(
  () => ({}),
  { verifyEmailOtp, resendEmailOtp },
)(EmailOtpModal)
